import { useState } from 'react'

import { t } from 'i18next'
import { FormProvider } from 'react-hook-form'
import { connect } from 'scrivito'

import { PRODUCT_TYPES, PREDEFINED_TASKS } from './constants'
import { Header } from './Header'
import { useLogTimeForm } from './hooks/useLogTimeForm'
import { useTimeRecordData } from './hooks/useTimeRecordData'
import { LogTimeForm } from './LogTimeForm'
import { TimeRecordDataService } from './services/timeRecordDataService'
import { Button } from '@/Atomic/Molecules/Button'
import {
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from '@/Atomic/Templates/Drawer'
import { showToast } from '@/Components/Toast'
import { ToastMessageType } from '@/Components/Toast/const'
import { PreFillData } from '@/hooks/useLogTimeDrawer'
import { useActivityTypes } from '@/Widgets/TimeTrackingOverviewWidget/hooks/useActivityTypes'
import { ConfirmActionModal } from '@/Widgets/TimeTrackingOverviewWidget/tabs/components/ConfirmActionModal'

interface LogTimeDrawerProps {
  close: () => void
  currentUserId: string
  recordId?: string | null
  preFillData?: PreFillData | null
  isLeader?: boolean
}

const sortByLabel = <T extends { label: string }>(options: T[]): T[] =>
  [...options].sort((a, b) => a.label.localeCompare(b.label))

const LogTimeDrawerInner = ({
  close,
  recordId,
  preFillData,
  currentUserId,
  isLeader = false,
}: LogTimeDrawerProps) => {
  const { customers } = useTimeRecordData(currentUserId)
  const typesOfActivity = useActivityTypes()

  const [showDiscardModal, setShowDiscardModal] = useState(false)

  const {
    methods,
    handleSaveClick,
    isSubmitting,
    isDirty,
    trigger,
    selectedCustomerId,
    selectedOrderId,
    users,
  } = useLogTimeForm({
    recordId,
    preFillData,
    currentUserId,
    isLeader,
    onSuccess: () => {
      showToast(
        ToastMessageType.SUCCESS,
        recordId
          ? t('timeTracking.notifications.timeEntryUpdatedSuccess')
          : t('timeTracking.notifications.timeEntryCreated'),
      )
      close()
    },
    onFailure: () => {
      showToast(
        ToastMessageType.ERROR,
        recordId
          ? t('timeTracking.notifications.timeEntryUpdatedError')
          : t('timeTracking.notifications.timeEntryCreatedError'),
      )
      close()
    },
  })

  const guardedClose = () => {
    if (isDirty) {
      setShowDiscardModal(true)
      return
    }
    close()
  }

  const confirmDiscard = () => {
    setShowDiscardModal(false)
    close()
  }

  const orders = selectedCustomerId
    ? sortByLabel(
        TimeRecordDataService.getOrders(selectedCustomerId).map((o) => ({
          label: o.name,
          value: o.id,
        })),
      )
    : []

  const workPackages = selectedOrderId
    ? sortByLabel(
        TimeRecordDataService.getPositions(selectedOrderId).map((p) => ({
          label: p.open != null ? `${p.name} > ${p.open}h` : p.name,
          value: p.id,
          position: p.position,
          budget: p.budget || undefined,
          open: p.open,
        })),
      )
    : []

  return (
    <>
      <DrawerHeader>
        <Header onClose={guardedClose} />
      </DrawerHeader>
      <DrawerBody>
        <FormProvider {...methods}>
          <LogTimeForm
            customers={sortByLabel(customers)}
            typesOfActivity={sortByLabel(typesOfActivity)}
            productsTypes={PRODUCT_TYPES}
            predefinedTasks={PREDEFINED_TASKS}
            orders={orders}
            workPackages={workPackages}
            users={sortByLabel(users)}
            isEditMode={!!recordId}
            currentUserId={currentUserId}
            isLeader={isLeader}
            triggerValidation={trigger}
          />
        </FormProvider>
      </DrawerBody>
      <DrawerFooter align="right">
        <Button variant="outlined" onClick={guardedClose}>
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSaveClick} disabled={isSubmitting}>
          {isSubmitting && (
            <div
              className="spinner border-2"
              style={{ height: '1rem', width: '1rem' }}
              role="status"
            />
          )}
          {t('common.save')}
        </Button>
      </DrawerFooter>

      <ConfirmActionModal
        isOpen={showDiscardModal}
        title={t('timeTracking.unsavedEntries.title')}
        message={t('timeTracking.unsavedEntries.message')}
        confirmText={t('timeTracking.unsavedEntries.confirm')}
        cancelText={t('common.cancel')}
        onConfirm={confirmDiscard}
        onClose={() => setShowDiscardModal(false)}
      />
    </>
  )
}

export const LogTimeDrawer = connect(LogTimeDrawerInner)
